import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useParams, Link, useLocation } from "react-router-dom";
import { RootState, RootDispatch } from "../../store";
import { format } from "date-fns";
import AddItemsComponent from "./AddItemsToBOM";
import {
  getAllBOMs,
  handleUpdateBOM,
  updateFullBOMLocally,
} from "../../store/slices/bomSlice";
import toast from "react-hot-toast";
import { CheckCircle, Trash2, XCircle } from "lucide-react";
import Button, { ButtonType } from "../../components/Assets/Button";
import BDMBoardSkeleton from "../../components/Skeleton/BDMBordSkeleton";
import { IBOMItem } from "../../models/bom.model";
import { getUnitsByType } from "../../utils/warehouse.util";

const BOMDetail = () => {
  const { bomId } = useParams();
  const dispatch = useDispatch<RootDispatch>();
  const { loading } = useSelector((state: RootState) => state.billOfMaterials);

  const { selectedBOM: bom } = useSelector(
    (state: RootState) => state.billOfMaterials,
  );
  const [showAddItemsModal, setShowAddItemsModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedBOM, setEditedBOM] = useState<{
    name: string;
    description: string;
    items: IBOMItem[];
  }>({
    name: "",
    description: "",
    items: [],
  });

  const location = useLocation();
  const from = location.state?.from;

  useEffect(() => {
    if (bom && !isEditing) {
      setEditedBOM({
        name: bom.name,
        description: bom.description || "",
        items: bom.items.map((item) => ({ ...item })),
      });
    }
  }, [bom, isEditing]);

  const handleEditToggle = () => {
    setIsEditing(!isEditing);
  };

  const handleFieldChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setEditedBOM((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleItemUpdate = (
    index: number,
    field: keyof IBOMItem,
    value: any,
  ) => {
    const newItems = editedBOM.items.map((it, idx) =>
      idx === index ? { ...it, [field]: value } : it,
    );
    setEditedBOM((prev) => ({ ...prev, items: newItems }));
  };

  const handleRemoveItem = (index: number) => {
    const newItems = [...editedBOM.items];
    newItems.splice(index, 1);
    setEditedBOM((prev) => ({
      ...prev,
      items: newItems,
    }));
  };

  const handleSave = async () => {
    try {
      if (!bomId) return;

      // 1. Update LOCAL Redux store immediately (Visual change)
      dispatch(
        updateFullBOMLocally({
          bomId,
          data: editedBOM,
        }),
      );

      // Close edit mode immediately to show the "saved" look
      setIsEditing(false);

      const updateData = {
        name: editedBOM.name,
        description: editedBOM.description,
        items: editedBOM.items.map((item) => ({
          product: item.product?._id || item.rawMaterial?._id,
          type: item.type,
          quantity: item.quantity,
          unit: item.displayUnit,
        })),
      };

      // 2. Send to backend asynchronously (Background sync)
      // The slice is configured to IGNORE the result of this call in the store
      await dispatch(handleUpdateBOM({ bomId, bomData: updateData })).unwrap();

      toast.success("BOM updated successfully");
    } catch (error) {
      toast.error("Failed to update BOM");
    }
  };

  const handleCancel = () => {
    if (bom) {
      setEditedBOM({
        name: bom.name,
        description: bom.description || "",
        items: bom.items.map((item) => ({ ...item })),
      });
    }
    setIsEditing(false);
  };

  if (loading) {
    return <BDMBoardSkeleton />;
  }

  if (!bom) {
    return <div className="container mx-auto px-4 py-8">BOM not found</div>;
  }

  const totalCost = editedBOM.items.reduce((sum, item) => {
    const price = item.product?.price || 0;
    return sum + item.quantity * price;
  }, 0);

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex items-center mb-6">
        <Link
          to={from === "priceList" ? "/price-list" : "/boms"}
          className="flex items-center text-gray-600 hover:text-gray-800 mr-4 text-sm"
        >
          ← Back
        </Link>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-xl shadow-md border border-gray-100 p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-gray-800">
                BOM Summary
              </h2>
              {!isEditing ? (
                <button
                  onClick={handleEditToggle}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                  </svg>
                </button>
              ) : (
                <div className="flex gap-2">
                  <Button
                    type={ButtonType.Cancel}
                    onClick={handleCancel}
                    className="flex items-center gap-2 text-xs"
                  >
                    <XCircle size={14} /> Cancel
                  </Button>
                  <Button
                    type={ButtonType.Extra}
                    onClick={handleSave}
                    className="flex items-center gap-2 text-xs"
                  >
                    <CheckCircle size={14} /> Save
                  </Button>
                </div>
              )}
            </div>

            {isEditing ? (
              <div className="space-y-4">
                <div>
                  <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1">
                    Name
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={editedBOM.name}
                    onChange={handleFieldChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1">
                    Description
                  </label>
                  <textarea
                    name="description"
                    value={editedBOM.description}
                    onChange={handleFieldChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm outline-none focus:ring-2 focus:ring-blue-500"
                    rows={3}
                  />
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <div>
                  <span className="text-[10px] font-bold text-gray-400 uppercase block">
                    Name
                  </span>
                  <h1 className="text-base font-bold text-gray-900">
                    {editedBOM.name}
                  </h1>
                </div>
                <div>
                  <span className="text-[10px] font-bold text-gray-400 uppercase block">
                    Description
                  </span>
                  <p className="text-sm text-gray-600">
                    {editedBOM.description || "No description provided"}
                  </p>
                </div>
              </div>
            )}
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-800">
                Components
              </h2>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-[10px] font-bold text-gray-500 uppercase">
                      Item
                    </th>
                    <th className="px-6 py-3 text-left text-[10px] font-bold text-gray-500 uppercase">
                      Type
                    </th>
                    <th className="px-6 py-3 text-left text-[10px] font-bold text-gray-500 uppercase">
                      Qty
                    </th>
                    <th className="px-6 py-3 text-left text-[10px] font-bold text-gray-500 uppercase">
                      Unit Price
                    </th>
                    <th className="px-6 py-3 text-right text-[10px] font-bold text-gray-500 uppercase">
                      Total
                    </th>
                    {isEditing && (
                      <th className="px-6 py-3 text-right text-[10px] font-bold text-gray-500 uppercase">
                        Action
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100 text-xs">
                  {editedBOM.items.map((item, index) => {
                    const itemName =
                      item.product?.productName || item.rawMaterial?.name;
                    const price = item.product?.price || 0;
                    const units =
                      item.type === "material"
                        ? getUnitsByType(item.rawMaterial?.unitType || "")
                        : [];

                    return (
                      <tr
                        key={index}
                        className="hover:bg-gray-50 transition-colors"
                      >
                        <td className="px-6 py-4">
                          <div className="font-bold text-gray-900">
                            {itemName}
                          </div>
                          <div className="text-[10px] text-gray-400">
                            {item.product?.hsn || "N/A"}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span
                            className={`px-2 py-0.5 rounded text-[9px] font-bold uppercase ${item.type === "product" ? "bg-blue-50 text-blue-600" : "bg-green-50 text-green-600"}`}
                          >
                            {item.type}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          {isEditing ? (
                            <div className="flex items-center gap-2">
                              <input
                                type="number"
                                min="0.01"
                                step="0.01"
                                value={item.quantity}
                                onChange={(e) =>
                                  handleItemUpdate(
                                    index,
                                    "quantity",
                                    parseFloat(e.target.value) || 0,
                                  )
                                }
                                className="w-16 px-2 py-1 border border-gray-300 rounded text-center outline-none"
                              />
                              {item.type === "material" ? (
                                <select
                                  value={item.displayUnit}
                                  onChange={(e) =>
                                    handleItemUpdate(
                                      index,
                                      "displayUnit",
                                      e.target.value,
                                    )
                                  }
                                  className="px-2 py-1 border border-gray-300 rounded text-[10px] outline-none"
                                >
                                  {units.map((u) => (
                                    <option key={u.value} value={u.value}>
                                      {u.label}
                                    </option>
                                  ))}
                                </select>
                              ) : (
                                <span className="text-gray-400">Units</span>
                              )}
                            </div>
                          ) : (
                            <span className="font-bold text-gray-700">
                              {item.quantity} {item.displayUnit || "Units"}
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-gray-500">
                          {price > 0 ? `₹${price.toFixed(2)}` : "—"}
                        </td>
                        <td className="px-6 py-4 text-right font-bold text-gray-900">
                          {price > 0
                            ? `₹${(item.quantity * price).toFixed(2)}`
                            : "—"}
                        </td>
                        {isEditing && (
                          <td className="px-6 py-4 text-right">
                            <button
                              onClick={() => handleRemoveItem(index)}
                              className="text-gray-300 hover:text-red-500 transition-colors"
                            >
                              <Trash2 size={16} />
                            </button>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <Button
            type={ButtonType.Extra}
            onClick={() => setShowAddItemsModal(true)}
            className="w-full h-[42px] bg-navyBlue-primary text-white"
          >
            Add Components
          </Button>

          <div className="bg-white rounded-xl shadow border border-gray-100 p-5">
            <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-4">
              BOM Metadata
            </h3>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-xs text-gray-500">Total Items</span>
                <span className="text-sm font-bold text-gray-900">
                  {editedBOM.items.length}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-xs text-gray-500">Created At</span>
                <span className="text-sm font-bold text-gray-900">
                  {format(new Date(bom.createdAt || ""), "MMM dd, yyyy")}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-xs text-gray-500">Last Updated</span>
                <span className="text-sm font-bold text-gray-900">
                  {format(new Date(bom.updatedAt || ""), "MMM dd, yyyy")}
                </span>
              </div>
              <div className="pt-4 border-t border-gray-100 flex justify-between items-center">
                <span className="text-xs font-bold text-gray-800">
                  Est. Product Cost
                </span>
                <span className="text-lg font-black text-blue-600">
                  ₹{totalCost.toFixed(2)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showAddItemsModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
              <h3 className="text-lg font-bold text-gray-900">
                Add Items to {editedBOM.name}
              </h3>
              <button
                onClick={() => setShowAddItemsModal(false)}
                className="p-2 hover:bg-white rounded-full text-gray-400 hover:text-gray-900 transition-all"
              >
                <XCircle size={20} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto">
              <AddItemsComponent
                bomId={bomId!}
                onSuccess={() => {
                  setShowAddItemsModal(false);
                  dispatch(getAllBOMs());
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BOMDetail;
